import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense, memo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme, APP_THEMES } from '../contexts/ThemeContext';
import { usePro, PRO_FEATURES } from '../contexts/ProContext';
import { useReadingProgress } from '../contexts/ReadingProgressContext';
import { languages } from '../i18n';
import { ChevronRight, ChevronDown, ChevronUp, Play, Star, Sparkles, Palette, Sun, Moon, BookOpen, Globe, Layers, Clock, Crown, Flame, Search, Menu, X, Home, Settings, Compass, MapPin, Bookmark, Radio, HandHeart, Scan, TrendingUp, ZoomIn, BookText, Mic } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
// PERFORMANCE: Virtualization for 114 Surah cards
import { FixedSizeList as VirtualList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
// PERFORMANCE: Static surah data for instant render (no API wait)
import { STATIC_SURAHS, TOTAL_SURAHS } from '../data/staticSurahs';
import ClassicPrayerRow from '../components/ClassicPrayerRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Juz to Surah mapping (approximate start surah for each juz)
const JUZ_MAPPING = [
  { juz: 1, startSurah: 1, startAyah: 1 },
  { juz: 2, startSurah: 2, startAyah: 142 },
  { juz: 3, startSurah: 2, startAyah: 253 },
  { juz: 4, startSurah: 3, startAyah: 93 },
  { juz: 5, startSurah: 4, startAyah: 24 },
  { juz: 6, startSurah: 4, startAyah: 148 },
  { juz: 7, startSurah: 5, startAyah: 83 },
  { juz: 8, startSurah: 6, startAyah: 111 },
  { juz: 9, startSurah: 7, startAyah: 88 },
  { juz: 10, startSurah: 8, startAyah: 41 },
  { juz: 11, startSurah: 9, startAyah: 93 },
  { juz: 12, startSurah: 11, startAyah: 6 },
  { juz: 13, startSurah: 12, startAyah: 53 },
  { juz: 14, startSurah: 15, startAyah: 1 },
  { juz: 15, startSurah: 17, startAyah: 1 },
  { juz: 16, startSurah: 18, startAyah: 75 },
  { juz: 17, startSurah: 21, startAyah: 1 },
  { juz: 18, startSurah: 23, startAyah: 1 },
  { juz: 19, startSurah: 25, startAyah: 21 },
  { juz: 20, startSurah: 27, startAyah: 56 },
  { juz: 21, startSurah: 29, startAyah: 46 },
  { juz: 22, startSurah: 33, startAyah: 31 },
  { juz: 23, startSurah: 36, startAyah: 28 },
  { juz: 24, startSurah: 39, startAyah: 32 },
  { juz: 25, startSurah: 41, startAyah: 47 },
  { juz: 26, startSurah: 46, startAyah: 1 },
  { juz: 27, startSurah: 51, startAyah: 31 },
  { juz: 28, startSurah: 58, startAyah: 1 },
  { juz: 29, startSurah: 67, startAyah: 1 },
  { juz: 30, startSurah: 78, startAyah: 1 },
];

// Kids theme card colors
const KIDS_CARD_COLORS = [
  { bg: 'bg-gradient-to-br from-pink-100 to-pink-200', border: 'border-pink-300', text: 'text-pink-600' },
  { bg: 'bg-gradient-to-br from-cyan-100 to-cyan-200', border: 'border-cyan-300', text: 'text-cyan-600' },
  { bg: 'bg-gradient-to-br from-yellow-100 to-amber-200', border: 'border-yellow-300', text: 'text-amber-600' },
  { bg: 'bg-gradient-to-br from-purple-100 to-purple-200', border: 'border-purple-300', text: 'text-purple-600' },
  { bg: 'bg-gradient-to-br from-green-100 to-emerald-200', border: 'border-green-300', text: 'text-green-600' },
  { bg: 'bg-gradient-to-br from-orange-100 to-orange-200', border: 'border-orange-300', text: 'text-orange-600' },
];

// Memoized Surah Card component for performance
const SurahCard = React.memo(({ surah, index, appTheme, isLastRead, t, settings }) => {
  const isMeccan = surah.revelation_place === 'meccan';
  const translatedName = t(`surahNames.${surah.id}`, { defaultValue: surah.translated_name?.name || surah.name_simple });
  const scriptStyleLabel = settings?.arabicScript === 'indopak' ? 'Indo-Pak' : 'Madinah';
  
  // Limit animation delay for cards beyond viewport
  const animationDelay = `${Math.min(index, 12) * 0.02}s`;

  if (appTheme === 'kids') {
    const colorSet = KIDS_CARD_COLORS[index % KIDS_CARD_COLORS.length];
    return (
      <Link
        to={`/quran/${surah.id}`}
        data-testid={`surah-card-${surah.id}`}
        className="animate-fade-in-up block group"
        style={{ opacity: 0, animationFillMode: 'forwards', animationDelay }}
      >
        <div className={`relative rounded-3xl ${colorSet.bg} border-3 ${colorSet.border} p-4 md:p-6 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg cursor-pointer overflow-hidden`}>
          <div className="absolute top-2 left-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/80 text-xs font-bold" style={{ fontFamily: "'Baloo 2', sans-serif", color: colorSet.text.replace('text-', '#').replace('-600', '') }}>
              <Star className="h-3 w-3 fill-current" />
              Surah Adventure
            </span>
          </div>
          <div className="flex items-center gap-3 md:gap-5 mt-6">
            <div className={`w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-white/80 flex flex-col items-center justify-center shadow-md`}>
              <span className={`text-xl md:text-2xl font-bold ${colorSet.text}`} style={{ fontFamily: "'Baloo 2', sans-serif" }}>
                {surah.id}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-base md:text-lg lg:text-xl text-gray-800 truncate md:whitespace-normal md:overflow-visible" style={{ fontFamily: "'Baloo 2', sans-serif" }}>
                  {surah.name_simple}
                </h3>
                {isLastRead && <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />}
              </div>
              <p className="text-sm md:text-base text-gray-600 truncate md:whitespace-normal md:overflow-visible">{translatedName}</p>
              <div className="flex items-center gap-2 mt-1 md:mt-2">
                <span className={`text-xs md:text-sm px-2 md:px-3 py-0.5 md:py-1 rounded-full font-medium bg-white/70 ${isMeccan ? 'text-amber-600' : 'text-green-600'}`}>
                  {isMeccan ? t('quran.makkah') : t('quran.madinah')}
                </span>
                <span className="text-xs md:text-sm text-gray-500">{surah.verses_count} {t('quran.ayat')}</span>
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <p className="font-arabic text-xl md:text-2xl lg:text-3xl text-gray-700">{surah.name_arabic}</p>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (appTheme === 'emerald') {
    return (
      <Link
        to={`/quran/${surah.id}`}
        data-testid={`surah-card-${surah.id}`}
        className="animate-fade-in-up block group"
        style={{ opacity: 0, animationFillMode: 'forwards', animationDelay }}
      >
        <div className="surah-card-emerald emerald-quran-surah-card relative overflow-hidden transition-all duration-300 hover:scale-[1.015] hover:shadow-xl cursor-pointer">
          <div className="emerald-border-bar h-1.5" />
          <div className="absolute top-2 left-2 w-5 h-5 border-l-2 border-t-2 emerald-corner-accent" />
          <div className="absolute top-2 right-2 w-5 h-5 border-r-2 border-t-2 emerald-corner-accent" />
          <div className="absolute bottom-2 left-2 w-5 h-5 border-l-2 border-b-2 emerald-corner-accent" />
          <div className="absolute bottom-2 right-2 w-5 h-5 border-r-2 border-b-2 emerald-corner-accent" />
          <CardContent className="relative p-4 sm:p-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="relative flex-shrink-0">
                <div className="emerald-number-badge w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-md">
                  <span className="emerald-number-text font-serif text-base font-bold">{surah.id}</span>
                </div>
                {isLastRead && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#c9a227] rounded-full flex items-center justify-center">
                    <Star className="h-2 w-2 text-white fill-white" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="emerald-title-text font-serif font-bold text-base sm:text-lg truncate">{surah.name_simple}</h3>
                <p className="emerald-subtitle-text text-xs truncate">{translatedName}</p>
                <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isMeccan ? 'emerald-badge-makkah' : 'emerald-badge-madinah'}`}>
                    {isMeccan ? t('quran.makkah') : t('quran.madinah')}
                  </span>
                  <span className="emerald-meta-text text-[10px]">{surah.verses_count} {t('quran.ayat')}</span>
                </div>
              </div>
              <div className="flex-shrink-0 text-right pl-2">
                <p className="emerald-arabic-text font-arabic text-xl sm:text-2xl">{surah.name_arabic}</p>
              </div>
            </div>
          </CardContent>
          <div className="emerald-border-bar h-1.5" />
        </div>
      </Link>
    );
  }

  if (appTheme === 'royal') {
    return (
      <Link
        to={`/quran/${surah.id}`}
        data-testid={`surah-card-${surah.id}`}
        className="animate-fade-in-up block"
        style={{ opacity: 0, animationFillMode: 'forwards', animationDelay }}
      >
        <div className="surah-card-royal relative overflow-visible transition-all duration-300 hover:scale-[1.01] hover:shadow-lg cursor-pointer">
          <div className="royal-corner-tl" />
          <div className="royal-corner-tr" />
          <div className="royal-corner-bl" />
          <div className="royal-corner-br" />
          <CardContent className="relative p-3 py-4 md:p-6 lg:p-7">
            <div className="flex items-center gap-2 md:gap-5">
              <div className="relative flex-shrink-0">
                <div className="royal-number-badge w-10 h-10 md:w-16 md:h-16 flex items-center justify-center">
                  <span className="font-serif text-lg md:text-2xl font-bold royal-badge-number">{surah.id}</span>
                </div>
                {isLastRead && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#c9a227] rounded-full flex items-center justify-center">
                    <Star className="h-2.5 w-2.5 text-white fill-white" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif font-bold text-sm md:text-lg lg:text-xl royal-title-text truncate md:whitespace-normal md:overflow-visible">{surah.name_simple}</h3>
                <p className="text-xs md:text-base royal-subtitle-text truncate md:whitespace-normal md:overflow-visible">{translatedName}</p>
                <div className="flex items-center gap-1.5 mt-1 md:mt-2">
                  <span className={`text-[10px] md:text-sm px-1.5 md:px-3 py-0.5 md:py-1 rounded-full font-medium royal-badge-madinah`}>
                    {scriptStyleLabel}
                  </span>
                  <span className="text-[10px] md:text-sm royal-subtitle-text">{surah.verses_count} {t('quran.ayat')}</span>
                </div>
              </div>
              <div className="flex-shrink-0 pl-1">
                <p className="font-arabic text-lg md:text-2xl lg:text-3xl royal-arabic-text text-right">{surah.name_arabic}</p>
              </div>
            </div>
          </CardContent>
        </div>
      </Link>
    );
  }

  // Classic theme
  return (
    <Link
      to={`/quran/${surah.id}`}
      data-testid={`surah-card-${surah.id}`}
      className="animate-fade-in-up block"
      style={{ opacity: 0, animationFillMode: 'forwards', animationDelay }}
    >
      <Card className={`card-islamic group relative overflow-hidden rounded-2xl border transition-all duration-500 hover:scale-[1.02] cursor-pointer ${
        isLastRead 
          ? 'bg-gradient-to-br from-yellow-500/10 via-amber-500/5 to-orange-500/10 ring-2 ring-yellow-500/30 border-yellow-500/30' 
          : 'bg-[#F1EBDD] border-[#C9A227]/40 hover:border-[#C9A227] hover:shadow-lg hover:shadow-[#C9A227]/10'
      }`}>
        <div className={`absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl ${
          isMeccan 
            ? 'bg-gradient-to-r from-transparent via-amber-500 to-transparent' 
            : 'bg-gradient-to-r from-transparent via-[#0F4C3A] to-transparent'
        }`} />
        <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
          isMeccan 
            ? 'bg-gradient-to-br from-amber-500/5 to-transparent' 
            : 'bg-gradient-to-br from-[#0F4C3A]/5 to-transparent'
        }`} />
        <CardContent className="relative p-3 sm:p-4 md:p-5">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative flex-shrink-0">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl transition-all duration-300 ${
                isMeccan 
                  ? 'bg-gradient-to-br from-amber-500/15 to-yellow-500/10 group-hover:from-amber-500 group-hover:to-yellow-600' 
                  : 'bg-[#0F4C3A]/15 group-hover:bg-[#0F4C3A]'
              }`}>
                <span className={`font-serif text-base sm:text-lg md:text-xl font-bold transition-colors duration-300 ${
                  isMeccan 
                    ? 'text-amber-600 dark:text-amber-400 group-hover:text-white' 
                    : 'text-[#0F4C3A] group-hover:text-[#C9A227]'
                }`}>
                  {surah.id}
                </span>
              </div>
              {isLastRead && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center shadow-lg shadow-yellow-500/50 animate-pulse-subtle">
                  <Star className="h-2.5 w-2.5 text-white fill-white" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm sm:text-base md:text-lg text-[#0F4C3A] truncate group-hover:text-[#0F4C3A] transition-colors">
                {surah.name_simple}
              </h3>
              <p className="text-xs sm:text-sm text-[#4A4A4A] truncate">{translatedName}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium ${
                  isMeccan 
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' 
                    : 'bg-[#0F4C3A]/10 text-[#0F4C3A]'
                }`}>
                  {isMeccan ? t('quran.makkah') : t('quran.madinah')}
                </span>
                <span className="text-[10px] sm:text-xs text-[#4A4A4A]">{surah.verses_count} {t('quran.ayat')}</span>
              </div>
            </div>
            <div className="flex-shrink-0 text-right pl-1">
              <p className="font-arabic text-lg sm:text-xl md:text-2xl text-[#0F4C3A]/80 group-hover:text-[#0F4C3A] transition-colors">
                {surah.name_arabic}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
});

// PERFORMANCE: Virtualized cell renderer for react-window Grid
const VirtualizedSurahCell = memo(({ columnIndex, rowIndex, style, data }) => {
  const { surahs, columnCount, appTheme, settings, t, cardWidth, cardHeight } = data;
  const index = rowIndex * columnCount + columnIndex;
  
  // Don't render if index is out of bounds
  if (index >= surahs.length) {
    return null;
  }
  
  const surah = surahs[index];
  
  return (
    <div style={{ ...style, padding: '0 8px 8px 0' }}>
      <SurahCard
        surah={surah}
        index={index}
        appTheme={appTheme}
        isLastRead={surah.id === settings.lastReadSurah}
        t={t}
        settings={settings}
      />
    </div>
  );
});

const QuranPage = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const { appTheme, setAppTheme, theme, toggleTheme, mode } = useTheme();
  const { isPro, promptUpgrade, isFeatureUnlockedViaAd, requestFeatureAccess } = usePro();
  const { progress, formatTime, getCompletionPercentage, getTodayTime } = useReadingProgress();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // PERFORMANCE: Initialize with static data for instant render (no loading state needed)
  const [surahs, setSurahs] = useState(STATIC_SURAHS);
  const [loading, setLoading] = useState(false); // No loading - static data ready instantly
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [juzDialogOpen, setJuzDialogOpen] = useState(false);
  const [progressCollapsed, setProgressCollapsed] = useState(true); // Start collapsed for faster initial render
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Hamburger menu state
  
  // Check if hamburger menu should be shown
  const showHamburgerMenu = !settings.floatingMenuStyle || settings.floatingMenuStyle === 'hamburger';
  
  // Next Prayer widget state - deferred loading
  const [nextPrayer, setNextPrayer] = useState(null);
  const [nextPrayerTime, setNextPrayerTime] = useState(null);
  const [prayerLoading, setPrayerLoading] = useState(true);
  
  // Pro-gated Night Vision toggle - STRICT PRO ONLY (NO AD UNLOCK)
  const handleNightVisionToggle = () => {
    // If currently in light mode, switching to dark requires Pro (NO ad unlock)
    if (mode === 'light') {
      if (!isPro) {
        // Night Vision is PRO ONLY - show upgrade prompt, no ad option
        requestFeatureAccess({ 
          ...PRO_FEATURES.NIGHT_VISION,
          noAdUnlock: true // Signal that ad unlock is not available
        });
        return;
      }
    }
    // If in dark mode, can always switch back to light (free)
    toggleTheme();
  };
  
  // Memoized filtered surahs (no search, just use surahs directly)
  const filteredSurahs = useMemo(() => surahs, [surahs]);
  
  // Memoized completion percentage to avoid recalculation
  const completionPercentage = useMemo(() => getCompletionPercentage(), [progress]);
  const todayTime = useMemo(() => getTodayTime(), [progress]);

  // Navigate to Mushaf (Quran) mode
  const openMushafMode = () => {
    const surahToOpen = settings.lastReadSurah || 1;
    const mushafPath = `/quran/mushaf/${surahToOpen}`;

    // Mushaf remains a Pro/earned-access reader. Request access here instead
    // of sending free users through a route that immediately returns home.
    if (!isPro && !isFeatureUnlockedViaAd(PRO_FEATURES.ZOOM_MODE.id)) {
      requestFeatureAccess(PRO_FEATURES.ZOOM_MODE, () => navigate(mushafPath));
      return;
    }

    navigate(`/quran/mushaf/${surahToOpen}`);
  };

  const handleThemeChange = (themeId) => {
    const isFreeTheme = themeId === 'classic';
    // Theme Selection is STRICT PRO ONLY (NO AD UNLOCK)
    const hasAccess = isPro;
    
    if (!hasAccess && !isFreeTheme) {
      // Close theme dialog first, then show PRO modal
      setThemeDialogOpen(false);
      // Themes are PRO ONLY - no ad unlock option
      requestFeatureAccess({
        ...PRO_FEATURES.ALL_THEMES,
        noAdUnlock: true // Signal that ad unlock is not available
      }, () => {
        setAppTheme(themeId);
        toast.success(`Theme: ${APP_THEMES[themeId].name}`);
      });
      return;
    }
    setAppTheme(themeId);
    setThemeDialogOpen(false);
    toast.success(`Theme: ${APP_THEMES[themeId].name}`);
  };

  const handleLanguageChange = useCallback((langCode) => {
    i18n.changeLanguage(langCode);
    setLanguageDialogOpen(false);
    const lang = languages.find(l => l.code === langCode);
    // Direction is now handled centrally in i18n/index.js
    toast.success(`Language: ${lang?.nativeName || langCode}`);
  }, [i18n]);

  const currentLang = useMemo(() => 
    languages.find(l => l.code === i18n.language) || languages[0],
    [i18n.language]
  );

  // PERFORMANCE: Fetch remaining surahs in background (silent merge)
  // First 15 surahs already rendered from static data
  useEffect(() => {
    const fetchRemainingSurahs = async () => {
      try {
        const response = await axios.get(`${API}/quran/surahs`);
        if (response.data?.chapters && response.data.chapters.length > STATIC_SURAHS.length) {
          // Silently merge full list - user won't notice since static data is already displayed
          setSurahs(response.data.chapters);
        }
      } catch (error) {
        console.error('Error fetching remaining surahs:', error);
        // Static data remains - no visual impact
      }
    };
    // Defer fetch to after initial render is complete
    const timer = setTimeout(fetchRemainingSurahs, 100);
    return () => clearTimeout(timer);
  }, []);

  // Defer prayer time fetch - runs after initial render with delay
  useEffect(() => {
    // Delay prayer API call to prioritize surah list rendering
    const timer = setTimeout(() => {
      const fetchNextPrayer = async () => {
        try {
          // Check for saved mosque
          const savedMosque = localStorage.getItem('quranplus-saved-mosque');
          let prayerTimes = null;
          
          if (savedMosque) {
            const mosque = JSON.parse(savedMosque);
            const response = await axios.get(`${API}/mawaqit/prayer-times/${mosque.uuid}`);
            if (response.data?.success) {
              prayerTimes = response.data.times;
            }
          }
          
          // Fallback to default location prayer times
          if (!prayerTimes) {
            const response = await axios.get(`${API}/prayer-times`, {
              params: { latitude: 51.5074, longitude: -0.1278, method: 15 }
            });
            if (response.data?.data?.timings) {
              const timings = response.data.data.timings;
              prayerTimes = {
                Fajr: timings.Fajr?.split(' ')[0],
                Sunrise: timings.Sunrise?.split(' ')[0],
                Dhuhr: timings.Dhuhr?.split(' ')[0],
                Asr: timings.Asr?.split(' ')[0],
                Maghrib: timings.Maghrib?.split(' ')[0],
                Isha: timings.Isha?.split(' ')[0],
              };
            }
          }
          
          if (prayerTimes) {
            // Calculate next prayer
            const now = new Date();
            const prayers = ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
            
            for (const prayer of prayers) {
              const time = prayerTimes[prayer];
              if (time && time !== '--:--') {
                const [hours, minutes] = time.split(':').map(Number);
                const prayerDate = new Date();
                prayerDate.setHours(hours, minutes, 0, 0);
                
                if (prayerDate > now) {
                  setNextPrayer(prayer);
                  setNextPrayerTime(time);
                  setPrayerLoading(false);
                  return;
                }
              }
            }
            
            // If all prayers passed, next is tomorrow's Fajr
            setNextPrayer('Fajr');
            setNextPrayerTime(prayerTimes.Fajr);
          }
        } catch (error) {
          console.error('Error fetching next prayer:', error);
        }
        setPrayerLoading(false);
      };
      
      fetchNextPrayer();
    }, 500); // 500ms delay after mount
    
    return () => clearTimeout(timer);
  }, []);

  // Juz dialog render
  const renderJuzDialog = () => (
    <DialogContent className="sm:max-w-md max-h-[80vh]">
      <DialogHeader>
        <DialogTitle className="font-serif text-xl flex items-center gap-2">
          <Layers className="h-5 w-5" />
          {t('app.selectJuz')}
        </DialogTitle>
      </DialogHeader>
      <ScrollArea className="h-[50vh] pr-4">
        <div className="grid grid-cols-5 gap-2 py-2">
          {JUZ_MAPPING.map((juz) => (
            <Link
              key={juz.juz}
              to={`/quran/${juz.startSurah}`}
              onClick={() => setJuzDialogOpen(false)}
              data-testid={`juz-option-${juz.juz}`}
              className="aspect-square flex items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary/20 hover:from-primary hover:to-primary/80 hover:text-white transition-all font-bold text-lg border border-primary/20 hover:border-primary"
            >
              {juz.juz}
            </Link>
          ))}
        </div>
      </ScrollArea>
    </DialogContent>
  );

  // Navigation items for hamburger menu - SYNCED with PageHeader.jsx (14 items, correct order)
  const navItems = [
    { path: '/', label: t('nav.home', 'Home'), icon: Home },
    { path: '/quran', label: t('nav.quran', 'Quran'), icon: BookOpen },
    { path: '/prayer-times', label: t('nav.prayerTimes', 'Prayer Times'), icon: Clock },
    { path: '/duas', label: t('nav.duas', 'Duas'), icon: HandHeart },
    { path: '/hadith', label: t('nav.hadith', 'Hadith'), icon: BookText },
    { path: '/mosques', label: t('nav.masjidGuide', 'Masjid Guide'), icon: MapPin, isPro: true, featureId: 'mosque_finder' },
    { path: '/prayer-times#masjid-connect', label: t('nav.masjidConnect', 'Masjid Connect'), icon: Radio, isPro: true, featureId: 'masjid_connect' },
    { path: '/halal-scanner', label: t('nav.halalScanner', 'Halal Scanner'), icon: Scan, isPro: true, featureId: 'halal_scanner' },
    { path: '/progress', label: t('nav.progress', 'Progress'), icon: TrendingUp, isPro: true, featureId: 'reading_stats' },
    { path: '/quran/zoom/1', label: t('nav.zoom', 'Zoom'), icon: ZoomIn, isPro: true, featureId: 'zoom_mode' },
    { path: '/qibla', label: t('nav.qibla', 'Qibla'), icon: Compass },
    { path: '/bookmarks', label: t('nav.saved', 'Bookmarks'), icon: Bookmark },
    // Search hidden as per user request
    { path: '/settings', label: t('nav.settings', 'Settings'), icon: Settings },
  ];

  // Render mobile menu content for hamburger
  const renderMobileMenuContent = () => {
    // Royal Knight theme - elegant cream card design
    if (appTheme === 'royal') {
      return (
        <div className="flex h-full flex-col" style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)' }}>
          {/* Royal Knight Header */}
          <div className="p-6" style={{ background: 'linear-gradient(135deg, #2a2a4a 0%, #1e1e3a 100%)', borderBottom: '2px solid rgba(201, 162, 39, 0.4)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/images/tanzeel-logo.png" alt="Tanzeen +" className="h-10 w-10 object-contain" />
                <div>
                  <div style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: 700, fontFamily: 'serif', letterSpacing: '0.025em', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                    Tanzeen <span style={{ color: '#c9a227', fontWeight: 900 }}>+</span>
                  </div>
                  <div style={{ color: '#ffffff', fontSize: '10px', fontStyle: 'italic', letterSpacing: '0.05em', opacity: 0.9 }}>{t('app.tagline')}</div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} className="rounded-full h-8 w-8 text-white hover:bg-white/20">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-2 px-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                const isActive = location.pathname === item.path || 
                                (item.path === '/quran' && location.pathname.startsWith('/quran'));
                
                // Royal Knight cream card styling
                const navItemStyle = isActive 
                  ? {
                      background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #7c3aed 100%)',
                      boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                      border: '1px solid rgba(139, 92, 246, 0.5)',
                    }
                  : {
                      background: 'linear-gradient(135deg, #f5f0e6 0%, #ebe5d9 100%)',
                      border: '1px solid rgba(201, 162, 39, 0.4)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    };
                
                const iconContainerStyle = isActive 
                  ? {
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: '2px solid rgba(255, 255, 255, 0.4)',
                    }
                  : {
                      background: 'rgba(201, 162, 39, 0.2)',
                      border: '2px solid #b8860b',
                    };
                
                const textColor = isActive ? '#ffffff' : '#2d2a26';
                const iconColor = isActive ? '#ffffff' : '#10b981';
                
                return (
                  <Link
                    key={item.path}
                    to={isItemLocked ? '#' : item.path}
                    onClick={(e) => {
                      if (isItemLocked) {
                        e.preventDefault();
                        setMobileMenuOpen(false);
                        requestFeatureAccess({ id: item.featureId, name: item.label }, () => {
                          window.location.href = item.path;
                        });
                        return;
                      }
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-4 rounded-2xl px-4 py-4 transition-all duration-300 no-underline"
                    style={{...navItemStyle, textDecoration: 'none'}}
                  >
                    <div className="rounded-xl p-2.5 flex-shrink-0" style={iconContainerStyle}>
                      <Icon className="h-5 w-5" style={{ color: iconColor, stroke: iconColor }} strokeWidth={2} />
                    </div>
                    <span 
                      className="flex-1 font-semibold text-base tracking-wide"
                      style={{ color: textColor, WebkitTextFillColor: textColor, textShadow: isActive ? '0 1px 2px rgba(0,0,0,0.3)' : 'none' }}
                    >
                      {item.label}
                    </span>
                    {isItemLocked && (
                      <span 
                        className="px-2 py-1 rounded-lg text-[9px] font-bold flex items-center gap-1 flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #c9a227 0%, #d4af37 100%)', color: '#0f0f1a', boxShadow: '0 2px 8px rgba(201, 162, 39, 0.3)' }}
                      >
                        <Crown className="h-2.5 w-2.5" />
                        PRO
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>
        </div>
      );
    }
    
    // Kids Paradise - Magical dreamy menu
    if (appTheme === 'kids') {
      return (
        <div 
          className="flex h-full flex-col relative overflow-hidden"
          style={{ background: 'linear-gradient(180deg, #fce7f3 0%, #e9d5ff 30%, #dbeafe 60%, #cffafe 100%)' }}
        >
          {/* Floating Stars/Sparkles decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-8 left-4 text-2xl animate-pulse">✨</div>
            <div className="absolute top-20 right-8 text-xl animate-bounce" style={{ animationDelay: '0.5s' }}>⭐</div>
            <div className="absolute top-40 left-8 text-lg animate-pulse" style={{ animationDelay: '1s' }}>🌟</div>
            <div className="absolute bottom-32 right-4 text-2xl animate-bounce" style={{ animationDelay: '0.3s' }}>✨</div>
            <div className="absolute bottom-20 left-6 text-xl animate-pulse" style={{ animationDelay: '0.7s' }}>⭐</div>
          </div>
          
          {/* Magical Rainbow Header */}
          <div 
            className="p-6 relative"
            style={{ background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #06b6d4 100%)' }}
          >
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img src="/images/tanzeel-logo.png" alt="Tanzeen +" className="h-10 w-10 object-contain" />
                  <span className="absolute -top-1 -right-1 text-sm">✨</span>
                </div>
                <div>
                  <span className="font-serif text-xl font-bold text-white drop-shadow-lg">
                    Tanzeen <span className="font-black" style={{fontWeight: 900}}>+</span>
                  </span>
                  <p className="text-xs text-white/90 italic">{t('app.tagline', 'Beyond Elegance')}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-full h-10 w-10 text-white hover:bg-white/20 backdrop-blur-sm"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
          
          {/* Magical Navigation */}
          <ScrollArea className="flex-1 py-4 relative z-10">
            <nav className="space-y-2 px-4">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                const isActive = location.pathname === item.path || 
                                (item.path === '/quran' && location.pathname.startsWith('/quran'));
                
                // Magical rainbow colors for each nav item
                const magicColors = [
                  { bg: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', shadow: 'rgba(236, 72, 153, 0.4)' },
                  { bg: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)', shadow: 'rgba(168, 85, 247, 0.4)' },
                  { bg: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)', shadow: 'rgba(6, 182, 212, 0.4)' },
                  { bg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', shadow: 'rgba(245, 158, 11, 0.4)' },
                  { bg: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', shadow: 'rgba(16, 185, 129, 0.4)' },
                  { bg: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', shadow: 'rgba(59, 130, 246, 0.4)' },
                  { bg: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', shadow: 'rgba(239, 68, 68, 0.4)' },
                  { bg: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', shadow: 'rgba(139, 92, 246, 0.4)' },
                ];
                const colorScheme = magicColors[index % magicColors.length];
                
                return (
                  <Link
                    key={item.path}
                    to={isItemLocked ? '#' : item.path}
                    onClick={(e) => {
                      if (isItemLocked) {
                        e.preventDefault();
                        setMobileMenuOpen(false);
                        requestFeatureAccess({ id: item.featureId, name: item.label }, () => {
                          window.location.href = item.path;
                        });
                        return;
                      }
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-4 rounded-2xl px-4 py-3.5 transition-all duration-300 ${
                      isActive ? '' : 'hover:scale-[1.02]'
                    }`}
                    style={{
                      background: isActive 
                        ? 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #06b6d4 100%)'
                        : 'rgba(255, 255, 255, 0.7)',
                      boxShadow: isActive 
                        ? '0 4px 20px rgba(168, 85, 247, 0.4), 0 0 30px rgba(236, 72, 153, 0.2)'
                        : '0 2px 8px rgba(0, 0, 0, 0.08)',
                      border: isActive 
                        ? '2px solid rgba(255, 255, 255, 0.3)'
                        : '2px solid rgba(168, 85, 247, 0.2)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <div 
                      className="rounded-xl p-2.5 flex-shrink-0"
                      style={{
                        background: isActive ? 'rgba(255, 255, 255, 0.3)' : colorScheme.bg,
                        boxShadow: isActive ? 'none' : `0 4px 12px ${colorScheme.shadow}`,
                      }}
                    >
                      <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                    </div>
                    <span 
                      className="flex-1 font-bold text-base"
                      style={{ color: isActive ? '#ffffff' : '#581c87' }}
                    >
                      {item.label}
                    </span>
                    {isItemLocked && (
                      <span 
                        className="px-2 py-1 rounded-full text-[9px] font-bold flex items-center gap-1"
                        style={{ 
                          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', 
                          color: '#7c2d12',
                          boxShadow: '0 2px 8px rgba(251, 191, 36, 0.4)'
                        }}
                      >
                        <Star className="h-2.5 w-2.5 fill-current" />
                        PRO
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>
          
          {/* Magical Footer */}
          <div className="p-4 border-t border-purple-200/50 relative z-10">
            <div 
              className="rounded-2xl p-4 text-center"
              style={{ background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)' }}
            >
              <p className="text-sm font-medium text-purple-600">
                ✨ Tanzeen + ✨
              </p>
              <p className="text-xs text-purple-400 mt-0.5">{t('app.tagline', 'Beyond Elegance')}</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Default menu for other themes - with colorful icons like homepage
    // Emerald theme: green gradient background with gold accents
    // Classic/Kids: white background matching homepage sidebar
    const isEmeraldTheme = appTheme === 'emerald';
    const menuBgClass = isEmeraldTheme 
      ? 'bg-gradient-to-b from-[#115e42] to-[#0d4a35]' 
      : 'bg-background';
    const headerBgClass = isEmeraldTheme 
      ? 'bg-gradient-to-r from-[#c9a227] to-[#d4b030]' 
      : 'bg-background border-b border-border';
    const headerTextClass = isEmeraldTheme ? 'text-black' : 'text-foreground';
    const closeButtonClass = isEmeraldTheme 
      ? 'text-black hover:bg-black/10' 
      : 'text-foreground hover:bg-muted';
    
    return (
      <div className={`flex h-full flex-col ${menuBgClass}`}>
        {/* Header */}
        <div className={`p-6 ${headerBgClass}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={((appTheme === 'classic' || appTheme === 'kids') && mode === 'light') 
                  ? "/images/tanzeel-logo-black.png" 
                  : "/images/tanzeel-logo.png"
                }
                alt="Tanzeen +" 
                className="h-8 w-8 object-contain" 
              />
              <div>
                <span className={`font-serif text-xl font-bold ${headerTextClass}`}>
                  Tanzeen <span className="font-black" style={{fontWeight: 900, WebkitTextStroke: "1px currentColor"}}>+</span>
                </span>
                <p className={`text-xs italic ${isEmeraldTheme ? 'text-black/70' : 'text-muted-foreground'}`}>{t('app.tagline', 'Beyond Elegance')}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} className={`rounded-xl h-10 w-10 ${closeButtonClass}`}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {navItems.map((item, index) => {
              const Icon = item.icon;
              const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
              const isActive = location.pathname === item.path || 
                              (item.path === '/quran' && location.pathname.startsWith('/quran'));
              
              // Colorful gradient icons for Classic/Kids themes
              const iconColors = [
                'from-blue-500 to-blue-600',
                'from-amber-500 to-orange-500',
                'from-emerald-500 to-teal-500',
                'from-pink-500 to-rose-500',
                'from-purple-500 to-indigo-500',
                'from-cyan-500 to-sky-500',
                'from-yellow-500 to-amber-500',
                'from-slate-500 to-gray-600',
              ];
              const iconGradient = iconColors[index % iconColors.length];
              
              // Emerald theme uses gold icons
              const isEmeraldTheme = appTheme === 'emerald';
              
              return (
                <Link
                  key={item.path}
                  to={isItemLocked ? '#' : item.path}
                  onClick={(e) => {
                    if (isItemLocked) {
                      e.preventDefault();
                      setMobileMenuOpen(false);
                      requestFeatureAccess({ id: item.featureId, name: item.label }, () => {
                        window.location.href = item.path;
                      });
                      return;
                    }
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-4 rounded-2xl px-4 py-4 transition-all ${
                    isActive
                      ? isEmeraldTheme 
                        ? 'bg-gradient-to-r from-[#c9a227] to-[#d4b030] shadow-lg'
                        : 'bg-primary text-primary-foreground shadow-lg'
                      : isEmeraldTheme
                        ? 'text-white hover:bg-white/10'
                        : 'hover:bg-muted/50'
                  }`}
                >
                  <div className={`rounded-xl p-2.5 ${
                    isActive 
                      ? (isEmeraldTheme ? 'bg-black/10' : 'bg-white/20')
                      : isEmeraldTheme 
                        ? 'bg-[#c9a227]/20'
                        : `bg-gradient-to-br ${iconGradient} shadow-md`
                  }`}>
                    <Icon 
                      className={`h-5 w-5 ${!isEmeraldTheme && !isActive ? 'text-white' : ''}`} 
                      style={{ color: isEmeraldTheme && !isActive ? '#c9a227' : undefined }}
                    />
                  </div>
                  <span className={`font-semibold text-base flex-1 ${
                    isEmeraldTheme 
                      ? (isActive ? 'text-black' : 'text-white')
                      : ''
                  }`}>
                    {item.label}
                  </span>
                  {isItemLocked && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center gap-0.5">
                      <Crown className="h-2.5 w-2.5" />
                      PRO
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>
        
        {/* Footer */}
        <div className="p-4 border-t border-border/30">
          <div className="rounded-2xl bg-muted/50 p-4 text-center">
            <p className="text-xs text-muted-foreground">
              Tanzeen <span className="font-black" style={{fontWeight: 900}}>+</span> | {t('app.tagline')}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Theme-specific header
  const renderHeader = () => {
    // Next Prayer pill widget - balanced size
    const NextPrayerPill = () => {
      if (!nextPrayer || !nextPrayerTime) return null;
      
      const pillBg = appTheme === 'emerald' 
        ? 'bg-amber-500/20 border-amber-400/40 text-amber-200' 
        : appTheme === 'royal' 
          ? 'bg-[#BF77FF]/20 border-[#BF77FF]/40 text-[#BF77FF]'
          : appTheme === 'kids'
            ? 'bg-pink-200 border-pink-300 text-pink-700'
            : 'bg-primary/10 border-primary/30 text-primary';
      
      return (
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${pillBg} text-xs font-medium`} data-testid="next-prayer-pill">
          <Clock className="h-3 w-3" />
          <span className="hidden sm:inline">{nextPrayer}</span>
          <span className="sm:hidden">{nextPrayer.slice(0,3)}</span>
          <span className="font-bold">{nextPrayerTime}</span>
        </div>
      );
    };

    if (appTheme === 'kids') {
      return (
        <div className="relative overflow-hidden kids-gradient-bg">
          {/* Sun decoration */}
          <div className="absolute -top-10 right-[15%] w-24 h-24 bg-gradient-to-br from-yellow-300 to-amber-400 rounded-full shadow-lg shadow-yellow-300/50 z-0" />
          {/* Clouds */}
          <div className="absolute top-8 left-10 w-20 h-8 bg-white rounded-full opacity-80" />
          <div className="absolute top-4 left-20 w-12 h-6 bg-white rounded-full opacity-60" />
          <div className="absolute top-10 right-10 w-16 h-7 bg-white rounded-full opacity-70" />
          
          <div className="relative px-3 py-2.5 z-10">
            <div className="mx-auto max-w-6xl">
              <div className="flex items-center gap-2">
                {/* Cute Quran icon - Clickable for Mushaf Mode */}
                <button
                  onClick={openMushafMode}
                  className="relative flex-shrink-0 group cursor-pointer"
                  title={t('quran.mushafMode', 'Open Quran Mode')}
                  data-testid="mushaf-mode-btn"
                >
                  <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-500 flex items-center justify-center shadow-lg group-hover:from-pink-300 group-hover:to-pink-400 transition-all group-hover:scale-105">
                    <BookOpen className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <Star className="absolute -top-1 -left-1 h-4 w-4 text-yellow-400 fill-yellow-400 animate-pulse" />
                </button>

                {/* Juz Button */}
                <Dialog open={juzDialogOpen} onOpenChange={setJuzDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-white px-3 sm:px-4 h-8 text-xs" data-testid="juz-btn">
                      <Layers className="h-3.5 w-3.5 sm:mr-1" />
                      <span className="hidden sm:inline">{t('app.juz')}</span>
                    </Button>
                  </DialogTrigger>
                  {renderJuzDialog()}
                </Dialog>

                <div className="flex-1" />

                {/* Buttons */}
                <div className="flex items-center gap-1.5">
                  <Dialog open={languageDialogOpen} onOpenChange={setLanguageDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-full bg-gradient-to-r from-green-400 to-teal-400 text-white text-xs px-2.5 h-8" data-testid="language-btn">
                        <Globe className="h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    {renderLanguageDialog()}
                  </Dialog>
                  {/* Dark Mode Toggle - Only visible for Pro users or if already in dark mode */}
                  {(isPro || mode === 'dark') && (
                    <Button 
                      size="sm" 
                      className="rounded-full bg-gradient-to-r from-slate-500 to-slate-600 text-white text-xs px-2.5 h-8 hidden lg:flex" 
                      onClick={handleNightVisionToggle}
                      data-testid="dark-mode-btn"
                    >
                      {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  {settings.lastReadSurah > 1 && (
                    <Link to={`/quran/${settings.lastReadSurah}`} data-testid="continue-reading-btn">
                      <Button size="sm" className="rounded-full bg-gradient-to-r from-cyan-400 to-cyan-500 text-white text-xs px-2.5 h-8">
                        <Play className="h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  )}
                  <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-full bg-gradient-to-r from-pink-400 to-purple-400 text-white text-xs px-2.5 h-8" data-testid="theme-btn">
                        <Palette className="h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    {renderThemeDialog()}
                  </Dialog>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (appTheme === 'emerald') {
      return (
        <div className="relative overflow-hidden bg-gradient-to-br from-[#115e42] via-[#0d4a35] to-[#0a3d2c]">
          {/* Islamic pattern overlay */}
          <div className="absolute inset-0 islamic-emerald-pattern opacity-20" />
          
          <div className="relative px-3 py-2.5 z-10">
            <div className="mx-auto max-w-6xl">
              <div className="flex items-center gap-2">
                {/* Quran book icon - Clickable for Mushaf Mode */}
                <button
                  onClick={openMushafMode}
                  className="relative flex-shrink-0 group cursor-pointer"
                  title={t('quran.mushafMode', 'Open Quran Mode')}
                  data-testid="mushaf-mode-btn"
                >
                  <img src="/images/quran-book.png" alt="Tanzeen+" loading="lazy" className="h-11 w-11 sm:h-12 sm:w-12 object-contain group-hover:scale-105 transition-transform" />
                </button>

                {/* Juz Button */}
                <Dialog open={juzDialogOpen} onOpenChange={setJuzDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-lg border-amber-500/40 text-amber-200 hover:bg-amber-500/20 px-3 sm:px-4 h-8 sm:h-9 text-xs" data-testid="juz-btn">
                      <Layers className="h-3.5 w-3.5 sm:mr-1" />
                      <span className="hidden sm:inline">{t('app.juz')}</span>
                    </Button>
                  </DialogTrigger>
                  {renderJuzDialog()}
                </Dialog>

                <div className="flex-1" />

                {/* Buttons */}
                <div className="flex items-center gap-1.5">
                  <Dialog open={languageDialogOpen} onOpenChange={setLanguageDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-lg border-amber-500/40 text-amber-200 hover:bg-amber-500/20 h-8 text-xs px-2.5" data-testid="language-btn">
                        <Globe className="h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    {renderLanguageDialog()}
                  </Dialog>
                  {/* Dark Mode Toggle */}
                  {/* Dark Mode Toggle - Only for Pro users */}
                  {(isPro || mode === 'dark') && (
                    <Button 
                      variant="outline"
                      size="sm" 
                      className="rounded-lg border-amber-500/40 text-amber-200 hover:bg-amber-500/20 h-8 text-xs px-2.5 hidden lg:flex" 
                      onClick={handleNightVisionToggle}
                      data-testid="dark-mode-btn"
                    >
                      {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  {settings.lastReadSurah > 1 && (
                    <Link to={`/quran/${settings.lastReadSurah}`} data-testid="continue-reading-btn">
                      <Button size="sm" className="rounded-full bg-[#c9a227] text-[#0d4a35] text-xs px-2.5 h-8">
                        <Play className="h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  )}
                  <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-lg border-amber-500/40 text-amber-200 hover:bg-amber-500/20 h-8 text-xs px-2.5" data-testid="theme-btn">
                        <Palette className="h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    {renderThemeDialog()}
                  </Dialog>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Royal Night theme header - dark navy blue with gold accents
    if (appTheme === 'royal') {
      return (
        <div className="relative overflow-hidden bg-gradient-to-br from-[#1e2a4a] via-[#1a2540] to-[#141c32]">
          <div className="relative px-4 py-1 z-10">
            <div className="mx-auto max-w-6xl">
              {/* Top Row: Logo + Title + Tagline on LEFT, Moon + Hamburger on RIGHT */}
              <div className="flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 cursor-pointer hover:opacity-90 transition-opacity">
                  <img 
                    src="/images/tanzeel-logo.png" 
                    alt="T+" 
                    className="h-8 w-8 object-contain"
                  />
                  <div>
                    <h1 className="text-base font-bold text-white">Tanzeen +</h1>
                    <p className="beyond-elegance-text text-[8px] italic whitespace-nowrap" style={{ color: 'white' }}>{t('common.beyondElegance', "Beyond Elegance")}</p>
                  </div>
                </Link>
                {/* Right side: Moon icon + Hamburger menu */}
                <div className="flex items-center gap-2">
                  {/* Night Vision toggle - Only for Pro users */}
                  {(isPro || mode === 'dark') && (
                    <button 
                      onClick={handleNightVisionToggle}
                      className="text-white hover:text-gray-300 transition-colors"
                      data-testid="dark-mode-btn"
                    >
                      {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                  )}
                  {/* Hamburger Menu - Only when hamburger style is selected */}
                  {showHamburgerMenu && (
                    <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                      <SheetTrigger asChild>
                        <Button variant="default" size="icon" className="rounded-xl h-10 w-10 bg-purple-600 hover:bg-purple-700" data-testid="quran-hamburger-menu">
                          <Menu className="h-5 w-5 text-white" />
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="right" className="w-80 p-0">
                        {renderMobileMenuContent()}
                      </SheetContent>
                    </Sheet>
                  )}
                </div>
              </div>
              
              {/* Icon Row: BookOpen + Juz on LEFT, Globe + Palette on RIGHT */}
              <div className="flex items-center justify-between mt-1.5">
                <div className="flex items-center gap-2">
                  <button
                    onClick={openMushafMode}
                    className="flex-shrink-0 group cursor-pointer"
                    title={t('quran.mushafMode', 'Open Quran Mode')}
                    data-testid="mushaf-mode-btn"
                  >
                    <BookOpen className="h-5 w-5 text-white group-hover:scale-105 transition-transform" />
                  </button>
                  <Dialog open={juzDialogOpen} onOpenChange={setJuzDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="icon" className="rounded-lg bg-[#c9a227] text-[#1e2a4a] hover:bg-[#d4b030] h-7 w-7" data-testid="juz-btn">
                        <Layers className="h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    {renderJuzDialog()}
                  </Dialog>
                </div>
                <div className="flex items-center gap-2">
                  <Dialog open={languageDialogOpen} onOpenChange={setLanguageDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="icon" className="rounded-full border border-[#c9a227] text-[#c9a227] hover:bg-[#c9a227]/20 h-7 w-7" data-testid="language-btn">
                        <Globe className="h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    {renderLanguageDialog()}
                  </Dialog>
                  <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="icon" className="rounded-full border border-[#c9a227] text-[#c9a227] hover:bg-[#c9a227]/20 h-7 w-7" data-testid="theme-btn">
                        <Palette className="h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    {renderThemeDialog()}
                  </Dialog>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div 
                className="mt-1.5 flex items-center gap-3 bg-[#1e2a4a]/80 rounded-xl px-4 py-1.5 cursor-pointer"
                onClick={() => navigate('/progress')}
                data-testid="header-progress-bar"
              >
                <div className="flex-1">
                  <Progress 
                    value={completionPercentage} 
                    className="h-2"
                    indicatorClassName="!bg-[#8B5CF6]"
                  />
                </div>
                <span className="text-white text-xs font-medium">
                  {completionPercentage}%
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Classic theme header - Uses shared ClassicPrayerRow (matches homepage + all other classic pages)
    return <ClassicPrayerRow />;
  };

  const renderThemeDialog = () => (
    <DialogContent className="sm:max-w-md" data-theme-modal="true">
      <DialogHeader>
        <DialogTitle className="font-serif text-xl">{t('common.chooseTheme', "Choose Theme")}</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-4">
        {Object.values(APP_THEMES).map((theme) => {
          const isSelected = appTheme === theme.id;
          const isFreeTheme = theme.id === 'classic';
          // Theme Selection is STRICT PRO ONLY (NO AD UNLOCK)
          const hasAccess = isPro;
          const isLocked = !hasAccess && !isFreeTheme;
          
          return (
            <button
              key={theme.id}
              onClick={() => handleThemeChange(theme.id)}
              data-testid={`theme-option-${theme.id}`}
              data-theme-card="true"
              className={`relative text-left rounded-xl border-2 overflow-hidden transition-all ${
                isSelected
                  ? 'border-primary shadow-lg ring-2 ring-primary/20'
                  : 'border-border hover:border-primary/50'
              } ${isLocked ? 'opacity-80' : ''}`}
            >
              <div className="h-12 flex">
                <div className="flex-1" style={{ backgroundColor: theme.preview.background }} />
                <div className="w-4" style={{ backgroundColor: theme.preview.primary }} />
                <div className="w-4" style={{ backgroundColor: theme.preview.secondary }} />
              </div>
              <div className="p-2 bg-[#f5f0e6]">
                <p className="text-xs font-semibold !text-black" style={{ color: '#000000 !important' }}>{theme.name}</p>
                <p className="text-[10px] !text-gray-700" style={{ color: '#374151 !important' }}>{theme.description}</p>
              </div>
              {isSelected && (
                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
              {/* PRO badge for locked themes */}
              {isLocked && (
                <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] font-bold flex items-center gap-0.5">
                  <Crown className="h-2.5 w-2.5" />
                  PRO
                </div>
              )}
            </button>
          );
        })}
      </div>
    </DialogContent>
  );

  // Language dialog render
  const renderLanguageDialog = () => (
    <DialogContent className="sm:max-w-md max-h-[80vh]">
      <DialogHeader>
        <DialogTitle className="font-serif text-xl flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Select Language
        </DialogTitle>
      </DialogHeader>
      <ScrollArea className="h-[50vh] pr-4">
        <div className="space-y-1 py-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              data-testid={`language-option-${lang.code}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                i18n.language === lang.code
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-foreground'
              }`}
            >
              <span className="text-2xl">{lang.flag}</span>
              <div>
                <span className="font-medium block">{lang.nativeName}</span>
                <span className={`text-xs ${i18n.language === lang.code ? 'text-primary-foreground/80' : 'text-foreground/60'}`}>{lang.name}</span>
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>
    </DialogContent>
  );

  // Page background based on theme
  const getPageClasses = () => {
    if (appTheme === 'kids') return 'min-h-screen pb-24 lg:pb-8 kids-gradient-bg';
    if (appTheme === 'emerald') return 'emerald-quran-page min-h-screen pb-24 lg:pb-8 bg-gradient-to-b from-[#115e42] via-[#0d4a35] to-[#0a3d2c]';
    if (appTheme === 'royal') return 'min-h-screen pb-24 lg:pb-8 royal-page-bg';
    return 'min-h-screen pb-24 lg:pb-8';
  };

  const getSectionHeaderClasses = () => {
    if (appTheme === 'kids') return 'text-purple-800';
    if (appTheme === 'emerald') return 'text-amber-200';
    if (appTheme === 'royal') return 'text-amber-300';
    if (appTheme === 'classic') return 'text-[#0F4C3A]';
    return 'text-foreground';
  };

  return (
    <div className={getPageClasses()}>
      {renderHeader()}

      {/* Surah Grid */}
      <div className="mx-auto max-w-6xl px-4 py-3 sm:px-6 lg:px-8">
        {/* Reading Progress Summary Card - Collapsible (Hidden for Royal Night since header has progress bar) */}
        {/* Auto-collapses after 5 seconds, clicking navigates to Progress page */}
        {appTheme !== 'royal' && settings.showProgressCard && (progress.totalTimeSpent > 0 || progress.currentStreak > 0) && (
          <Card 
            className={`mb-4 overflow-hidden border transition-all cursor-pointer hover:shadow-md ${
              appTheme === 'emerald' 
                ? 'bg-[#0d4a35]/60 border-[#c9a227]/30 hover:border-[#c9a227]/50' 
                : appTheme === 'royal'
                  ? 'bg-[#1e2a4a]/60 border-[#c9a227]/30 hover:border-[#c9a227]/50'
                  : appTheme === 'kids'
                    ? 'bg-gradient-to-r from-pink-100 to-purple-100 border-purple-200'
                    : appTheme === 'classic'
                      ? 'bg-[#F1EBDD] border-[#C9A227]/40 hover:border-[#C9A227]'
                      : 'bg-card border-border/50 hover:border-primary/30'
            }`} 
            data-testid="progress-summary-card"
            onClick={() => navigate('/progress')}
          >
            <CardContent className={progressCollapsed ? "p-2" : "p-4"}>
              {/* Collapsed: Show only progress bar with expand arrow */}
              {progressCollapsed ? (
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Progress 
                      value={completionPercentage} 
                      className="h-2"
                      indicatorClassName={
                        appTheme === 'emerald' || appTheme === 'royal' ? '!bg-[#c9a227]' :
                        appTheme === 'classic' ? '!bg-[#0F4C3A]' : ''
                      }
                    />
                  </div>
                  <span className={`text-xs font-medium ${
                    appTheme === 'emerald' || appTheme === 'royal' ? '!text-white' : appTheme === 'classic' ? 'text-[#0F4C3A]' : 'text-foreground'
                  }`}>
                    {completionPercentage}%
                  </span>
                  <ChevronDown className={`h-4 w-4 ${
                    appTheme === 'emerald' || appTheme === 'royal' ? '!text-white' : appTheme === 'classic' ? 'text-[#0F4C3A]' : 'text-muted-foreground'
                  }`} />
                </div>
              ) : (
                /* Expanded: Show full stats */
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Today's Time */}
                      <div className="text-center">
                        <div className="flex items-center gap-1.5">
                          <Clock className={`h-4 w-4 ${
                            appTheme === 'emerald' || appTheme === 'royal' ? 'text-[#c9a227]' : 'text-primary'
                          }`} />
                          <span className={`text-lg font-bold ${
                            appTheme === 'emerald' || appTheme === 'royal' ? 'text-white' : ''
                          }`}>
                            {formatTime(todayTime)}
                          </span>
                        </div>
                        <span className={`text-[10px] ${
                          appTheme === 'emerald' || appTheme === 'royal' ? 'text-white/60' : 'text-muted-foreground'
                        }`}>
                          {t('progress.today', 'Today')}
                        </span>
                      </div>
                      
                      {/* Divider */}
                      <div className={`h-8 w-px ${
                        appTheme === 'emerald' || appTheme === 'royal' ? 'bg-white/20' : 'bg-border'
                      }`} />
                      
                      {/* Streak */}
                      <div className="text-center">
                        <div className="flex items-center gap-1.5">
                          <Flame className="h-4 w-4 text-orange-500" />
                          <span className={`text-lg font-bold ${
                            appTheme === 'emerald' || appTheme === 'royal' ? 'text-white' : ''
                          }`}>
                            {progress.currentStreak}
                          </span>
                        </div>
                        <span className={`text-[10px] ${
                          appTheme === 'emerald' || appTheme === 'royal' ? 'text-white/60' : 'text-muted-foreground'
                        }`}>
                          {t('progress.streak', 'Streak')}
                        </span>
                      </div>
                      
                      {/* Divider */}
                      <div className={`h-8 w-px hidden sm:block ${
                        appTheme === 'emerald' || appTheme === 'royal' ? 'bg-white/20' : 'bg-border'
                      }`} />
                      
                      {/* Completion */}
                      <div className="hidden sm:block text-center">
                        <div className="flex items-center gap-1.5">
                          <BookOpen className={`h-4 w-4 ${
                            appTheme === 'emerald' || appTheme === 'royal' ? 'text-[#c9a227]' : 'text-primary'
                          }`} />
                          <span className={`text-lg font-bold ${
                            appTheme === 'emerald' || appTheme === 'royal' ? 'text-white' : ''
                          }`}>
                            {completionPercentage}%
                          </span>
                        </div>
                        <span className={`text-[10px] ${
                          appTheme === 'emerald' || appTheme === 'royal' ? 'text-white/60' : 'text-muted-foreground'
                        }`}>
                          {t('progress.complete', 'Complete')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Expand/Collapse Arrow - prevents navigation when clicked */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Don't navigate, just toggle
                          setProgressCollapsed(!progressCollapsed);
                        }}
                        className="p-1 rounded-full hover:bg-black/10"
                        aria-label={t('quran.collapseProgress', "Collapse progress card")}
                        data-testid="progress-summary-toggle"
                      >
                        <ChevronUp className={`h-5 w-5 ${
                          appTheme === 'emerald' || appTheme === 'royal' ? 'text-white/50' : 'text-muted-foreground'
                        }`} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Progress Bar at bottom */}
                  <div className="mt-3">
                    <Progress 
                      value={completionPercentage} 
                      className="h-1.5"
                      indicatorClassName={appTheme === 'emerald' || appTheme === 'royal' ? '!bg-[#c9a227]' : ''}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Section Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {appTheme === 'kids' ? (
              <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
            ) : appTheme === 'emerald' ? (
              <Sparkles className="h-4 w-4 text-amber-400" />
            ) : appTheme === 'royal' ? (
              <Sparkles className="h-4 w-4 text-amber-400" />
            ) : appTheme === 'classic' ? (
              <Sparkles className="h-4 w-4 text-[#C9A227]" />
            ) : (
              <Sparkles className="h-4 w-4 text-yellow-500" />
            )}
            <h2 className={`font-serif text-base font-bold ${getSectionHeaderClasses()}`} style={appTheme === 'kids' ? { fontFamily: "'Baloo 2', sans-serif", fontSize: '1.25rem' } : {}}>
              {t('quran.allSurahs')}
            </h2>
          </div>
          <div className={`text-xs ${appTheme === 'emerald' ? 'text-amber-300/70' : appTheme === 'royal' ? 'text-amber-300/70' : appTheme === 'kids' ? 'text-purple-600' : appTheme === 'classic' ? 'text-[#4A4A4A]' : 'text-muted-foreground'}`}>
            {appTheme === 'kids' ? (
              <span className="flex items-center gap-1 bg-white/70 px-3 py-1 rounded-full font-bold" style={{ fontFamily: "'Baloo 2', sans-serif" }}>
                <Sun className="h-4 w-4 text-yellow-500" />
                {filteredSurahs.length}
              </span>
            ) : appTheme === 'royal' ? (
              <span className="text-amber-300">{filteredSurahs.length} {t('quran.surahs')}</span>
            ) : (
              <span>{filteredSurahs.length} {t('quran.surahs')}</span>
            )}
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-180px)]">
          {loading ? (
            <div className="grid gap-3 md:gap-4 lg:gap-5 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
              {[...Array(12)].map((_, i) => (
                <Skeleton key={i} className="h-28 md:h-36 lg:h-40 rounded-2xl" />
              ))}
            </div>
          ) : filteredSurahs.length > 0 ? (
            /* PERFORMANCE: Using standard grid with memo'd SurahCard for now */
            /* Full virtualization would require significant layout changes */
            <div className={`grid gap-3 md:gap-4 lg:gap-5 ${appTheme === 'kids' ? 'sm:grid-cols-1 lg:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3'}`}>
              {filteredSurahs.map((surah, index) => (
                <SurahCard
                  key={surah.id}
                  surah={surah}
                  index={index}
                  appTheme={appTheme}
                  isLastRead={surah.id === settings.lastReadSurah}
                  t={t}
                  settings={settings}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-24 h-24 rounded-full bg-muted/50 flex items-center justify-center mb-6">
                <Search className="h-10 w-10 text-muted-foreground" />
              </div>
              <p className="text-xl font-semibold text-foreground">{t('quran.noSurahsFound')}</p>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export default QuranPage;
